import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLayout } from '@/providers/Layout.provider';
import PaymentMethodForm from './PaymentMethodForm';
import { usePaylink } from '@/providers/Paylink.provider';
import {
  Box,
  Icon,
  Button,
  formattedCurrency,
  sanitizeToNumeric,
  getLuminance,
  getContrastColor,
  withStyles,
} from 'vopay';
import styles from './PaymentMethod.module.scss';

const cn = withStyles(styles);

export type PaymentMethodProps = {
  paymentMethodKey: string;
  isOpen: boolean;
  Header: React.JSX.Element;
  onRemove?: () => void;
  onUpdate?: (amount: string) => void;
  onUpdatingChange?: (isUpdating: boolean) => void;
  onClick: () => void;
  account?: React.JSX.Element | string;
  amountRemaining?: number;
  amount?: number;
  disabled?: boolean;
};

const PaymentMethod: React.FC<PaymentMethodProps> = ({
  isOpen,
  Header,
  onRemove,
  onUpdate,
  account,
  amountRemaining,
  amount,
  paymentMethodKey,
  onClick,
  disabled = false,
  onUpdatingChange,
}: PaymentMethodProps) => {
  const [isUpdating, setIsUpdating] = useState(false);
  const setUpdating = (value: boolean) => {
    setIsUpdating(value);
    onUpdatingChange?.(value);
  };
  const { color } = useLayout();
  const { paylink } = usePaylink();
  const { t } = useTranslation('translations', { keyPrefix: 'MultiplePaymentMethods' });
  return (
    <div
      onClick={() => {
        if (!isOpen && !disabled) {
          onClick();
        }
      }}
      onKeyDown={() => {}}
      className={cn('containerPayment', disabled && 'disabled', isOpen && 'isOpen')}
      role="button"
      tabIndex={0}
    >
      <Box
        base="8"
        pt={isOpen ? 'lmd' : 'md'}
        pb={isOpen ? 'lmd' : 'md'}
        pe="smd"
        ps="smd"
        gap="lmd"
        flx="col"
        boundary="shadow"
        r="sm"
        maxwidth="100%"
      >
        <Box flx="row" jc="sb" ai="center">
          <span className={cn('containerPaymentBold')}>{Header}</span>
          {!isOpen && <Icon icon={isOpen ? 'angleTop' : 'angleRight'} height="14px" width="14px" />}
        </Box>
        {isOpen && (
          <div className={cn('containerItem')}>
            <Box base="8" gap="lmd" flx="col">
              <Box flx="row" jc="sb" ai="center">
                <div className={cn('containerPaymentBold')}>{account}</div>

                <Box flx="row" jc="sb" gapSize="10px" ai="center">
                  {!isUpdating && (
                    <span className={cn('containerPaymentBold')}>
                      {paylink?.Currency} {formattedCurrency(sanitizeToNumeric(amount?.toString()) || '')}
                    </span>
                  )}
                  {!isUpdating && (
                    <Button
                      label=""
                      id={`${paymentMethodKey}-edit`}
                      color="primary"
                      icon="edit"
                      iconLocation="center"
                      round
                      iconColor={getLuminance(color) > 0.5 ? 'primary-dark' : 'primary-light'}
                      iconHeight="9px"
                      iconWidth="9px"
                      height="21px"
                      width="21px"
                      borderRadius="50%"
                      noPadding
                      background={color}
                      textColor={getContrastColor(color)}
                      onClick={() => {
                        setUpdating(true);
                      }}
                    />
                  )}
                  {isUpdating && (
                    <Button
                      label={
                        <Box ps="sm" base="8" ai="center">
                          {`${t('remove')}`}
                        </Box>
                      }
                      id={`${paymentMethodKey}-edit`}
                      color="secondary"
                      iconHeight="11px"
                      iconWidth="11px"
                      height="32px"
                      iconLocation="left"
                      icon="trash"
                      round
                      noShadow
                      onClick={() => {
                        if (onRemove) onRemove();
                      }}
                    />
                  )}
                </Box>
              </Box>
              {isUpdating && (
                <PaymentMethodForm
                  defaultValues={{
                    Amount: amount?.toString() || '',
                    MaximumAmount: ((amountRemaining || 0) + (amount || 0))?.toString() || '',
                  }}
                  onSave={(_values) => {
                    setUpdating(false);
                    if (onUpdate) onUpdate(_values.Amount);
                  }}
                  onCancel={() => {
                    setUpdating(false);
                  }}
                />
              )}
            </Box>
          </div>
        )}
      </Box>
    </div>
  );
};

export default PaymentMethod;
